import React, { useState } from 'react';
import {
  FileText,
  Music,
  Image as ImageIcon,
  Share2,
  Edit3,
  CheckCircle2,
  AlertCircle,
  AlertTriangle,
  ArrowLeft,
  Send,
  Sparkles,
  ShieldCheck,
  Radio,
  FileCheck,
} from 'lucide-react';
import ValidationErrorsModal from './ValidationErrorsModal';
import { validateFullRelease } from '../../lib/distributionService';

export default function Step5Review({
  releaseData,
  audioTracks,
  coverArt,
  profile,
  onEditStep,
  onSubmit,
  isSubmitting,
  onBack,
  isEmailVerified = true,
  onRequestVerification,
}) {
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [isValidationModalOpen, setIsValidationModalOpen] = useState(false);

  const fullValidation = validateFullRelease(
    {
      ...releaseData,
      audioTracks,
      coverArt,
    },
    profile
  );

  const handleOpenConfirm = () => {
    if (!isEmailVerified) {
      if (onRequestVerification) onRequestVerification();
      return;
    }
    if (!fullValidation.isValid) {
      setIsValidationModalOpen(true);
      return;
    }
    setConfirmModalOpen(true);
  };

  const handleConfirmSubmit = () => {
    setConfirmModalOpen(false);
    onSubmit();
  };

  return (
    <div>
      {/* Grouped Validation Errors Modal */}
      <ValidationErrorsModal
        isOpen={isValidationModalOpen}
        onClose={() => setIsValidationModalOpen(false)}
        errors={fullValidation.errors}
        validationResult={fullValidation}
        onFocusField={(item) => {
          if (item.stepNumber && item.stepNumber !== 5) {
            onEditStep(item.stepNumber);
          }
        }}
      />

      <div className="dist-card">
        <div className="dist-card-header">
          <div>
            <h2 className="dist-card-title">
              <ShieldCheck size={22} color="#d9fb06" /> Review Your Release
            </h2>
            <p className="dist-card-desc">
              Please review all metadata, songwriter credits, and audio specifications before submitting for distribution.
            </p>
          </div>
        </div>

        {/* ── 1. RELEASE REVIEW SECTION ── */}
        <div className="dist-review-section">
          <div className="dist-review-section-header">
            <h3 className="dist-review-section-title">
              <FileText size={18} color="#d9fb06" /> Release Information
            </h3>
            <button
              type="button"
              className="dist-review-edit-btn"
              onClick={() => onEditStep(3)}
            >
              <Edit3 size={13} /> Edit Release
            </button>
          </div>

          <div className="dist-review-grid">
            <div className="dist-review-item">
              <span className="dist-review-label">Release Title</span>
              <span className="dist-review-value" style={{ color: '#d9fb06' }}>
                {releaseData.title || 'Untitled'}
              </span>
            </div>

            <div className="dist-review-item">
              <span className="dist-review-label">Creator Legal Name</span>
              <span className="dist-review-value" style={{ color: '#ffffff', fontWeight: 600 }}>
                {releaseData.legalName || ', '}
              </span>
            </div>

            <div className="dist-review-item">
              <span className="dist-review-label">Release Type</span>
              <span className="dist-review-value">
                {releaseData.releaseType?.toUpperCase()}
              </span>
            </div>

            <div className="dist-review-item">
              <span className="dist-review-label">Primary Genre</span>
              <span className="dist-review-value">{releaseData.genre}</span>
            </div>

            <div className="dist-review-item">
              <span className="dist-review-label">Mood</span>
              <span className="dist-review-value">{releaseData.mood}</span>
            </div>

            <div className="dist-review-item">
              <span className="dist-review-label">Primary Artist(s)</span>
              <span className="dist-review-value">
                {(releaseData.primaryArtists || []).filter(Boolean).join(', ') || 'None'}
              </span>
            </div>

            <div className="dist-review-item">
              <span className="dist-review-label">Featuring Artist(s)</span>
              <span className="dist-review-value">
                {(releaseData.featuringArtists || []).filter(Boolean).join(', ') || 'None'}
              </span>
            </div>

            <div className="dist-review-item">
              <span className="dist-review-label">Release Date</span>
              <span className="dist-review-value" style={{ color: '#d9fb06' }}>
                {releaseData.releaseDate}
              </span>
            </div>

            <div className="dist-review-item">
              <span className="dist-review-label">Release Time &amp; Timezone</span>
              <span className="dist-review-value">
                {releaseData.releaseTime || '00:00'} ({releaseData.releaseTimezone})
              </span>
            </div>

            <div className="dist-review-item">
              <span className="dist-review-label">C Line</span>
              <span className="dist-review-value">
                {releaseData.cLine || ', '}
              </span>
            </div>

            <div className="dist-review-item">
              <span className="dist-review-label">P Line</span>
              <span className="dist-review-value">
                {releaseData.pLine || ', '}
              </span>
            </div>

            <div className="dist-review-item">
              <span className="dist-review-label">Release ISRC / UPC</span>
              <span className="dist-review-value">
                {releaseData.isrc ? releaseData.isrc : 'Not assigned yet (available after A&R clarification)'}
              </span>
            </div>
          </div>
        </div>

        {/* ── 2. TRACKS REVIEW SECTION ── */}
        <div className="dist-review-section">
          <div className="dist-review-section-header">
            <h3 className="dist-review-section-title">
              <Music size={18} color="#d9fb06" /> Track Details &amp; Audio ({audioTracks.length})
            </h3>
            <button
              type="button"
              className="dist-review-edit-btn"
              onClick={() => onEditStep(3)}
            >
              <Edit3 size={13} /> Edit Tracks
            </button>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {audioTracks.map((audioTrack, idx) => {
              const trackData = releaseData.tracks?.[idx] || {};

              return (
                <div
                  key={idx}
                  style={{
                    background: 'rgba(255,255,255,0.03)',
                    border: '1px solid rgba(255,255,255,0.08)',
                    borderRadius: '10px',
                    padding: '1.2rem',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.8rem', flexWrap: 'wrap', gap: '0.5rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                      <span className="dist-track-num-pill">Track {idx + 1}</span>
                      <span style={{ fontSize: '1.05rem', fontWeight: 700, color: '#ffffff' }}>
                        {trackData.title || audioTrack.filename}
                      </span>
                    </div>

                    <div className="dist-track-tech-chips">
                      <span className="dist-tech-chip accent">44.1 kHz PCM WAV</span>
                      <span className="dist-tech-chip">{audioTrack.bitDepth || 16}-bit</span>
                      <span className="dist-tech-chip">⏱ {audioTrack.durationFormatted || '3:30'}</span>
                    </div>
                  </div>

                  <div className="dist-review-grid" style={{ marginTop: '0.6rem' }}>
                    <div className="dist-review-item">
                      <span className="dist-review-label">Version</span>
                      <span className="dist-review-value">{trackData.versionType || 'Original'}</span>
                    </div>

                    <div className="dist-review-item">
                      <span className="dist-review-label">Language</span>
                      <span className="dist-review-value">{trackData.language || 'Hindi'}</span>
                    </div>

                    <div className="dist-review-item">
                      <span className="dist-review-label">Vocal / Explicit</span>
                      <span className="dist-review-value">
                        {trackData.vocalType || 'Vocal'} • {trackData.explicit ? 'Explicit (18+)' : 'Clean'}
                      </span>
                    </div>

                    <div className="dist-review-item">
                      <span className="dist-review-label">Composer(s)</span>
                      <span className="dist-review-value">
                        {(trackData.composers || []).filter(Boolean).join(', ') || ', '}
                      </span>
                    </div>

                    <div className="dist-review-item">
                      <span className="dist-review-label">Lyricist(s)</span>
                      <span className="dist-review-value">
                        {(trackData.lyricists || []).filter(Boolean).join(', ') || ', '}
                      </span>
                    </div>

                    <div className="dist-review-item">
                      <span className="dist-review-label">Recording Origin</span>
                      <span className="dist-review-value">
                        {trackData.countryRecorded || 'India'} ({trackData.recordingYear || '2026'})
                      </span>
                    </div>

                    <div className="dist-review-item">
                      <span className="dist-review-label">AI Status</span>
                      <span className="dist-review-value">
                        {trackData.aiUsed ? (
                          <span style={{ color: '#d9fb06', display: 'inline-flex', alignItems: 'center', gap: '0.3rem' }}>
                            <FileCheck size={14} /> AI PDF Attached
                          </span>
                        ) : (
                          'No AI Used'
                        )}
                      </span>
                    </div>

                    <div className="dist-review-item">
                      <span className="dist-review-label">Sample Status</span>
                      <span className="dist-review-value">
                        {trackData.hasSamples ? `${trackData.samples?.length || 1} Sample(s) Declared` : 'No Samples'}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* ── 3. COVER ART REVIEW SECTION ── */}
        <div className="dist-review-section">
          <div className="dist-review-section-header">
            <h3 className="dist-review-section-title">
              <ImageIcon size={18} color="#d9fb06" /> Cover Artwork
            </h3>
            <button
              type="button"
              className="dist-review-edit-btn"
              onClick={() => onEditStep(4)}
            >
              <Edit3 size={13} /> Edit Cover
            </button>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem', flexWrap: 'wrap' }}>
            <div style={{ width: '120px', height: '120px', borderRadius: '10px', overflow: 'hidden', border: '2px solid rgba(217,251,6,0.4)' }}>
              <img
                src={coverArt?.previewUrl || coverArt?.url || ''}
                alt="Release Cover Art"
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            </div>

            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, color: '#ffffff', fontSize: '1rem', marginBottom: '0.3rem' }}>
                {coverArt?.originalFilename || 'Cover Artwork'}
              </div>
              <div className="dist-track-tech-chips">
                <span className="dist-tech-chip accent">3000 × 3000 px</span>
                <span className="dist-tech-chip">1:1 Square</span>
                <span className="dist-tech-chip">JPEG High-Res</span>
                <span className="dist-tech-chip">Verified ✓</span>
              </div>
            </div>
          </div>
        </div>

        {/* ── 4. SOCIAL MONETISATION REVIEW ── */}
        <div className="dist-review-section">
          <div className="dist-review-section-header">
            <h3 className="dist-review-section-title">
              <Share2 size={18} color="#d9fb06" /> Social Monetisation
            </h3>
            <button
              type="button"
              className="dist-review-edit-btn"
              onClick={() => onEditStep(3)}
            >
              <Edit3 size={13} /> Edit Socials
            </button>
          </div>

          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
            <div style={{ background: 'rgba(255,255,255,0.05)', padding: '0.6rem 1rem', borderRadius: '8px', fontSize: '0.85rem' }}>
              Instagram Music: <strong style={{ color: releaseData.socialMonetisation?.instagramMusic !== false ? '#d9fb06' : '#aaa' }}>{releaseData.socialMonetisation?.instagramMusic !== false ? 'ON' : 'OFF'}</strong>
            </div>
            <div style={{ background: 'rgba(255,255,255,0.05)', padding: '0.6rem 1rem', borderRadius: '8px', fontSize: '0.85rem' }}>
              TikTok: <strong style={{ color: releaseData.socialMonetisation?.tiktok !== false ? '#d9fb06' : '#aaa' }}>{releaseData.socialMonetisation?.tiktok !== false ? 'ON' : 'OFF'}</strong>
            </div>
            <div style={{ background: 'rgba(255,255,255,0.05)', padding: '0.6rem 1rem', borderRadius: '8px', fontSize: '0.85rem' }}>
              YouTube Content ID: <strong style={{ color: releaseData.socialMonetisation?.youtube !== false ? '#d9fb06' : '#aaa' }}>{releaseData.socialMonetisation?.youtube !== false ? 'ON' : 'OFF'}</strong>
            </div>
          </div>
        </div>

        {/* Email Verification Warning if unverified */}
        {!isEmailVerified && (
          <div
            style={{
              background: 'rgba(219, 255, 0, 0.1)',
              border: '1.5px solid rgba(219, 255, 0, 0.45)',
              borderRadius: '12px',
              padding: '1rem 1.25rem',
              marginTop: '1.5rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: '1rem',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <AlertTriangle size={22} style={{ color: '#DBFF00', flexShrink: 0 }} />
              <div>
                <div style={{ fontWeight: 600, color: '#ffffff', fontSize: '0.95rem' }}>
                  Email Verification Required
                </div>
                <div style={{ color: 'rgba(255, 255, 255, 0.75)', fontSize: '0.85rem' }}>
                  Please verify your email address to submit this release for distribution.
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={onRequestVerification}
              style={{
                background: '#DBFF00',
                color: '#111111',
                fontWeight: 700,
                fontSize: '0.85rem',
                padding: '0.55rem 1.1rem',
                borderRadius: '8px',
                border: 'none',
                cursor: 'pointer',
              }}
            >
              Verify Email Now
            </button>
          </div>
        )}

        {/* Footer Navigation & Final Submit Button */}
        <div className="dist-footer-actions">
          <button type="button" className="dist-btn-secondary" onClick={onBack}>
            <ArrowLeft size={18} /> Back to Cover Art
          </button>

          <button
            type="button"
            className="dist-btn-primary"
            onClick={handleOpenConfirm}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <span className="auth-spinner" /> Submitting Release...
              </>
            ) : (
              <>
                <Send size={18} /> Submit Release to SenritsuX
              </>
            )}
          </button>
        </div>
      </div>

      {/* Final Confirmation Modal */}
      {confirmModalOpen && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.75)',
            backdropFilter: 'blur(8px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '1rem',
          }}
          onClick={() => setConfirmModalOpen(false)}
        >
          <div
            style={{
              background: '#322f87',
              border: '1px solid rgba(217, 251, 6, 0.4)',
              borderRadius: '16px',
              padding: '2rem',
              maxWidth: '460px',
              width: '100%',
              boxShadow: '0 20px 50px rgba(0, 0, 0, 0.6)',
              textAlign: 'center',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                width: '60px',
                height: '60px',
                borderRadius: '50%',
                background: 'rgba(217,251,6,0.15)',
                color: '#d9fb06',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1.2rem',
              }}
            >
              <Radio size={30} />
            </div>

            <h3 style={{ fontSize: '1.35rem', fontWeight: 700, color: '#ffffff', margin: '0 0 0.5rem' }}>
              Confirm Release Submission
            </h3>

            <p style={{ fontSize: '0.88rem', color: 'rgba(255, 255, 255, 0.8)', lineHeight: 1.5, margin: '0 0 1.5rem' }}>
              Are you sure you want to submit <strong>&ldquo;{releaseData.title}&rdquo;</strong> for distribution?
              Our A&amp;R quality control team will inspect your master audio and deliver your music to 150+ digital streaming platforms by <strong>{releaseData.releaseDate}</strong>.
            </p>

            <div style={{ display: 'flex', gap: '0.8rem', justifyContent: 'center' }}>
              <button
                type="button"
                className="dist-btn-secondary"
                onClick={() => setConfirmModalOpen(false)}
              >
                Cancel &amp; Review
              </button>

              <button
                type="button"
                className="dist-btn-primary"
                onClick={handleConfirmSubmit}
              >
                Yes, Submit Release
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
